import React, { useState, useMemo, useEffect } from 'react';
import { Button, Space, Input, Form, Tag, Tooltip, Modal, Typography, notification, Col, Row } from 'antd';
import { Link, useNavigate } from 'react-router-dom';
import { SearchOutlined, EditOutlined, DeleteOutlined, CheckOutlined, CopyOutlined } from '@ant-design/icons';
import { CheckTableCustom } from '../../components/CheckTableCustom';
import { useMutation } from '@tanstack/react-query';
import CustomSelect from '../../components/CustomSelect';
import NormalFormLabel from '../../components/NormalFormLabel';
import { getListBannersRequest } from '../../services/bannerRequest';
import { useQuery } from '@tanstack/react-query';
import { LIST_APPEARANCE, LIST_STATUS_POPUP, LIST_BANNER_TYPE } from '../../constants/appSelect';
import { bannerStatusChangeRequest, deleteBannerRequest } from '../../services/bannerRequest';
import { newObjectKeys } from '../../utils/utility';
import { checkPermission } from '../../utils/checkPermission';
import { getListAllCampaignsRequest, getListAllPartnersRequest } from '../../services/campaignStatisticRequest';
import {
  BANNER_STATUS_CHANGE,
  CREATE_UPDATE_BANNER,
  DELETE_BANNER,
  GET_LIST_BANNERS,
} from '../../constants/apiEndPoint';
import { getListBannerPageRequest } from '../../services/commonRequest';
import { isEqual } from 'lodash';
import { useSelector } from 'react-redux';

const { Text } = Typography;

const QuanLyBanner = () => {
  const navigate = useNavigate();
  const [formSearch] = Form.useForm();
  const dataRedux = useSelector((state) => state.user);
  const accountPartnerId = dataRedux?.info?.partner_id;
  const accountType = dataRedux?.info?.type;
  const checkAccountIsPartner = accountType === 2 && accountPartnerId;
  const [params, setParams] = useState({
    pageId: 1,
    limit: 20,
    status: [0, 1, 3, 4],
  });

  const {
    data: dataBanners,
    refetch: refetchDataBanners,
    isPending,
  } = useQuery({
    queryKey: ['dataBanners', params],
    queryFn: () => getListBannersRequest(params),
    enabled: checkPermission(GET_LIST_BANNERS, 'Xem danh sách'),
  });

  useEffect(() => {
    const saved = sessionStorage.getItem('filterStateBanners');
    if (saved) {
      const filters = JSON.parse(saved);
      formSearch.setFieldsValue(filters);
      setParams(filters);
    }
  }, []);

  // get data campaigns
  const { data: dataAllCampaigns } = useQuery({
    queryKey: ['dataAllCampaignsBanner'],
    queryFn: () => getListAllCampaignsRequest(),
  });

  // convert list campaigns options
  const dataCampaignsOption = useMemo(() => {
    const options =
      dataAllCampaigns?.map((item) => {
        return { label: item?.name, value: item?.id };
      }) || [];
    return options;
  }, [dataAllCampaigns]);

  // get data partners
  const { data: dataAllPartners } = useQuery({
    queryKey: ['dataAllPartnersBanner'],
    queryFn: () => getListAllPartnersRequest(),
    enabled: !checkAccountIsPartner,
  });

  // convert list partners options
  const dataAllPartnersOption = useMemo(() => {
    const options =
      dataAllPartners?.map((item) => {
        return { label: item?.name, value: item?.id };
      }) || [];
    return options;
  }, [dataAllPartners]);

  // get data banners page
  const { data: dataAllBannerPage } = useQuery({
    queryKey: ['dataAllBannerPage'],
    queryFn: () => getListBannerPageRequest(),
  });

  // convert list banner page options
  const dataAllBannerPageOption = useMemo(() => {
    const options =
      dataAllBannerPage?.map((item) => {
        return { label: item?.name, value: item?.id?.toString() };
      }) || [];
    return options;
  }, [dataAllBannerPage]);

  const { mutate: mutateDeleteBanner } = useMutation({
    mutationFn: deleteBannerRequest,
    onSuccess: async (data) => {
      if (data?.status) {
        notification.success({
          message: 'Xóa banner thành công!',
        });
        refetchDataBanners();
      } else {
        notification.error({
          message: data?.showMessage || 'Có lỗi xảy ra vui lòng thử lại sau!',
        });
      }
    },
    onError: (error) => {
      notification.error({
        message: error?.data?.showMessage || 'Có lỗi xảy ra vui lòng thử lại sau!',
      });
    },
  });

  const handleConfirm = (record, actionType) => {
    let modalConfig = {};
    if (actionType === 'delete') {
      modalConfig = {
        content: 'Bạn có muốn xóa banner này không?',
        okText: 'Xác nhận',
        cancelText: 'Hủy',
        okType: 'danger',
        onOk() {
          mutateDeleteBanner(record.id);
        },
        onCancel() {},
      };
    } else if (actionType === 'approve') {
      modalConfig = {
        content: 'Bạn có muốn duyệt banner này?',
        okText: 'Xác nhận',
        cancelText: 'Hủy',
        onOk() {
          handleApprove(record);
        },
        onCancel() {},
      };
    } else {
      return;
    }
    Modal.confirm(modalConfig);
  };

  const { mutate: mutateChangeStatusBanner } = useMutation({
    mutationFn: bannerStatusChangeRequest,
    onSuccess: async (data) => {
      if (data?.status) {
        notification.success({
          message: 'Cập nhật trạng thái popup thành công!',
        });
        refetchDataBanners();
      } else {
        notification.error({
          message: data?.showMessage || 'Có lỗi xảy ra vui lòng thử lại sau!',
        });
      }
    },
  });

  const handleApprove = (data) => {
    // If receiving data from popup form (batch update)
    if (data.ids) {
      mutateChangeStatusBanner(data);
    }
    // If receiving a single record from table action
    else if (data.id) {
      const newStatus = data.status === 0 ? 1 : 0;
      mutateChangeStatusBanner({ id: data.id.toString(), status: newStatus });
    }
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'key',
      key: 'key',
      width: 70,
      fixed: 'left',
    },
    {
      title: 'Tiêu đề',
      dataIndex: ['title', 'id'],
      key: 'title',
      render: (_, { title, id }) => (
        <Link to={`/quan-ly-banner/${id}/chinh-sua-banner`} className="text-[#1677FF] hover:cursor-pointer">
          {title}
        </Link>
      ),
      minWidth: 150,
      width: 250,
      fixed: 'left',
    },
    {
      title: 'Chiến dịch quảng cáo',
      dataIndex: ['campaignName', 'campaign_id'],
      key: 'campaign_id',
      render: (_, { campaignName, campaign_id }) => (
        <Link
          to={`/quan-ly-chien-dich/${campaign_id}/chinh-sua-chien-dich`}
          className="text-[#1677FF] hover:cursor-pointer"
        >
          {campaignName}
        </Link>
      ),
      minWidth: 150,
      width: 250,
    },
    {
      title: 'Hình ảnh',
      dataIndex: 'image',
      key: 'image',
      width: 120,
      render: (image) => <img src={image} alt="" className="w-full h-auto object-contain" />,
    },
    {
      title: 'Tập khách hàng',
      dataIndex: ['platform', 'version', 'topic', 'audience', 'type_topic'],
      key: ['audience', 'topic'],
      render: (_, { platform, version, topic, audience, type_topic }) => (
        <Space direction="vertical">
          {platform && <Text>{platform}</Text>}
          {version && Array.isArray(version) && version?.map((item) => <Text>{item}</Text>)}
          {topic && Array.isArray(topic) && topic?.map((item) => <Text>{item}</Text>)}
          {audience && Array.isArray(audience) && audience?.map((item) => <Text>{item}</Text>)}
        </Space>
      ),
    },
    {
      title: 'Trang hiển thị',
      dataIndex: 'page',
      key: 'page',
      width: 100,
      render: (page) => dataAllBannerPageOption?.find((item) => item?.value === page)?.label,
    },
    {
      title: 'Loại banner',
      dataIndex: 'banner_type',
      key: 'banner_type',
      width: 100,
      render: (banner_type) => LIST_BANNER_TYPE?.find((item) => item?.value === banner_type)?.label,
    },
    {
      title: 'Thứ tự hiển thị',
      dataIndex: 'place',
      key: 'place',
      width: 90,
    },
    {
      title: 'Đối tác',
      dataIndex: 'partnerName',
      key: 'partnerName',
      minWidth: 100,
      width: 120,
    },
    {
      title: 'Số click tối đa',
      dataIndex: 'max_click',
      key: 'max_click',
      width: 120,
    },
    {
      title: 'Số click tối đa/ngày',
      dataIndex: 'max_click_per_date',
      key: 'max_click_per_date',
      width: 120,
    },
    {
      title: 'Số impression tối đa',
      dataIndex: 'max_impression',
      key: 'max_impression',
      width: 120,
    },
    {
      title: 'Số impression tối đa/ngày',
      dataIndex: 'max_impression_per_date',
      key: 'max_impression_per_date',
      width: 120,
    },
    {
      title: 'Lý do ẩn',
      dataIndex: 'visible_reason',
      key: 'visible_reason',
      render: (_, { visible_reason }) => (
        <Space direction="vertical">
          {visible_reason && Array.isArray(visible_reason) && visible_reason?.map((item) => <Text>{item}</Text>)}
        </Space>
      ),
      width: 120,
    },
    {
      title: 'Cập nhật lần cuối',
      dataIndex: 'modify_time',
      key: 'modify_time',
      minWidth: 90,
      width: 120,
      render: (_, { modify_byName, modify_time }) => (
        <Space direction="vertical">
          <Text>{modify_byName}</Text>
          <Text>{modify_time}</Text>
        </Space>
      ),
    },
    {
      title: 'Hiển thị',
      dataIndex: 'is_visible',
      key: 'is_visible',
      render: (is_visible) => (
        <Tag
          style={{
            backgroundColor: LIST_APPEARANCE.find((item) => item.value === is_visible)?.color,
            color: 'white',
            borderRadius: '8px',
            padding: '4px 10px',
          }}
        >
          {LIST_APPEARANCE.find((item) => item.value === is_visible)?.label}
        </Tag>
      ),
      fixed: 'right',
    },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag
          style={{
            backgroundColor: LIST_STATUS_POPUP.find((item) => item.value === status)?.color,
            color: 'white',
            borderRadius: '8px',
            padding: '4px 10px',
          }}
        >
          {LIST_STATUS_POPUP.find((item) => item.value === status)?.label}
        </Tag>
      ),
      width: 100,
      fixed: 'right',
    },
    {
      title: 'Thao tác',
      key: 'action',
      render: (_, record) => (
        <Space size="middle">
          {checkPermission(CREATE_UPDATE_BANNER, 'Chỉnh sửa') && (
            <Tooltip title="Chỉnh sửa banner">
              <EditOutlined onClick={() => navigate(`/quan-ly-banner/${record?.id}/chinh-sua-banner`)} />
            </Tooltip>
          )}
          {checkPermission(DELETE_BANNER, 'Xóa') && record?.status !== 2 && (
            <Tooltip title="Xóa banner">
              <DeleteOutlined onClick={() => handleConfirm(record, 'delete')} />
            </Tooltip>
          )}
          {checkPermission(CREATE_UPDATE_BANNER, 'Sao chép') && (
            <Tooltip title="Sao chép banner">
              <CopyOutlined onClick={() => navigate(`/quan-ly-banner/${record?.id}/sao-chep-banner`)} />
            </Tooltip>
          )}
          {checkPermission(BANNER_STATUS_CHANGE, 'Duyệt') && record?.status === 0 && (
            <Tooltip title="Duyệt banner">
              <CheckOutlined onClick={() => handleConfirm(record, 'approve')} />
            </Tooltip>
          )}
        </Space>
      ),
      width: 120,
      fixed: 'right',
      hidden:
        !checkPermission(CREATE_UPDATE_BANNER, 'Chỉnh sửa') &&
        !checkPermission(DELETE_BANNER, 'Xóa') &&
        !checkPermission(CREATE_UPDATE_BANNER, 'Sao chép') &&
        !checkPermission(BANNER_STATUS_CHANGE, 'Duyệt'),
    },
  ];

  const handleSubmitForm = (values) => {
    const temp = { pageId: 1, limit: 20 };

    if (values?.title) {
      Object.assign(temp, { title: values?.title });
    }

    if (values?.campaign_id?.length) {
      Object.assign(temp, { campaign_id: values?.campaign_id });
    }

    if (values?.partner_id?.length) {
      Object.assign(temp, { partner_id: values?.partner_id });
    }

    if (values?.platform >= 0) {
      Object.assign(temp, { platform: values?.platform });
    }

    if (values?.page?.length) {
      Object.assign(temp, { page: values?.page });
    }

    if (values?.status?.length) {
      Object.assign(temp, { status: values?.status });
    } else {
      Object.assign(temp, { status: [0, 1, 3, 4] });
    }

    if (values?.visible >= 0) {
      Object.assign(temp, { visible: values?.visible });
    }

    if (isEqual(temp, params)) {
      refetchDataBanners();
    } else {
      sessionStorage.setItem('filterStateBanners', JSON.stringify(temp));
      setParams(temp);
    }
  };

  return (
    <div className="m-4 ">
      <div className="flex justify-between items-center mb-5">
        <h1 className="font-semibold text-2xl font-roboto">Danh sách Banner</h1>
      </div>
      {checkPermission(GET_LIST_BANNERS, 'Xem danh sách') && (
        <>
          <Form
            name="search-banner"
            layout="vertical"
            form={formSearch}
            onFinish={handleSubmitForm}
            requiredMark={false}
          >
            <Row gutter={[16, 0]}>
              <Col xs={24} sm={12} lg={6}>
                <Form.Item label={<NormalFormLabel text="Tiêu đề banner" />} name="title">
                  <Input placeholder="Tìm kiếm theo tiêu đề bannner" />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} lg={6}>
                <Form.Item label={<NormalFormLabel text="Chiến dịch quảng cáo" />} name="campaign_id">
                  <CustomSelect placeholder="Chọn chiến dịch quảng cáo" mode="multiple" options={dataCampaignsOption} />
                </Form.Item>
              </Col>

              {!checkAccountIsPartner && (
                <Col xs={24} sm={12} lg={6}>
                  <Form.Item label={<NormalFormLabel text="Đối tác" />} name="partner_id">
                    <CustomSelect placeholder="Chọn đối tác" mode="multiple" options={dataAllPartnersOption} />
                  </Form.Item>
                </Col>
              )}

              <Col xs={24} sm={12} lg={6}>
                <Form.Item label={<NormalFormLabel text="Nền tảng" />} name="platform" initialValue={-1}>
                  <CustomSelect
                    placeholder="Chọn nền tảng"
                    options={[
                      { value: -1, label: 'Tất cả' },
                      { value: 1, label: 'IOS' },
                      { value: 2, label: 'Android' },
                    ]}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} lg={6}>
                <Form.Item label={<NormalFormLabel text="Vị trí hiển thị" />} name="page">
                  <CustomSelect placeholder="Chọn vị trí hiển thị" mode="multiple" options={dataAllBannerPageOption} />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} lg={6}>
                <Form.Item label={<NormalFormLabel text="Trạng thái" />} name="status">
                  <CustomSelect placeholder="Chọn trạng thái" mode="multiple" options={LIST_STATUS_POPUP} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Form.Item label={<NormalFormLabel text="Hiển thị" />} name="visible" initialValue={-1}>
                  <CustomSelect
                    placeholder="Hiển thị"
                    options={[
                      { value: -1, label: 'Tất cả' },
                      { value: 0, label: 'Ẩn' },
                      { value: 1, label: 'Hiện' },
                    ]}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Form.Item>
                  <Space direction="horizontal" className="sm:mt-7">
                    <Button type="primary" htmlType="submit">
                      <SearchOutlined /> Tìm kiếm
                    </Button>
                    {params &&
                      Object.values(
                        newObjectKeys(params, [
                          'title',
                          'campaign_id',
                          'page',
                          'platform',
                          'visible',
                          'status',
                          'partner_id',
                        ])
                      ).some((value) => (value && !Array.isArray(value)) || value?.length > 0) && (
                        <Button
                          htmlType="button"
                          onClick={() => {
                            formSearch.resetFields();

                            sessionStorage.setItem(
                              'filterStateBanners',
                              JSON.stringify({ pageId: 1, limit: params.limit, status: [0, 1, 3, 4] })
                            );

                            setParams({ pageId: 1, limit: params.limit, status: [0, 1, 3, 4] });
                          }}
                        >
                          <DeleteOutlined />
                          Xóa lọc
                        </Button>
                      )}
                  </Space>
                </Form.Item>
              </Col>
            </Row>
          </Form>

          <CheckTableCustom
            isLoading={isPending}
            columns={columns}
            dataSource={dataBanners?.data}
            totalItem={dataBanners?.totalItem}
            params={params}
            setParams={(params) => {
              setParams(params);
              sessionStorage.setItem('filterStateBanners', JSON.stringify(params));
            }}
            handleApprove={handleApprove}
            roles="banners"
          />
        </>
      )}
    </div>
  );
};

export default QuanLyBanner;
